import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  Input,
  Output,
  EventEmitter,
  HostListener,
} from '@angular/core';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { File } from '../../objects/file';
import { AppComponent } from '../../app.component';
import { UserPermissions } from '../../objects/permissions';
import { ListingModel } from '../../objects/listing-model';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { ApiService } from '../../api.service';
import { SharedDataService } from '../../services/shared-data.service';
import { OnDestroy } from '@angular/core';
import { SubscriptionLike } from 'rxjs';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { DirectLinkDialogComponent } from '../direct-link-dialog/direct-link-dialog.component';
import { ZipDialogComponent } from '../zip-dialog/zip-dialog.component';
import { RenameDialogComponent } from '../rename-dialog/rename-dialog.component';
import { UploadService } from '../../services/uploader.service';
import { DeleteConfirmationComponent } from '../delete-confirmation/delete-confirmation.component';
import { clearFocus } from '../../utils';
import { FS3Notification } from '../../objects/notification';
import {JobsListItem} from "../../objects/jobs";
import {ListItem} from "../../objects/listItem";
import {DialogCanvasComponent} from "../dialog-canvas/dialog-canvas.component";
import {AppEvent} from "../../objects/appEvent";
import { v4 as uuid } from 'uuid';
import {NavigationItem} from "../../objects/navigationItem";
import {environment} from "../../../environments/environment";

@Component({
  selector: 'app-listing',
  templateUrl: './listing.component.html',
  styleUrls: ['./listing.component.scss'],
})
export class ListingComponent implements OnInit, OnDestroy {
  @Input() set state(value: string) {
    this.updateInternalStates(value)
  }
  private updateInternalStates(value: string) {
    this.isClients = false;
    this.isArticlesFolders = false;
    this.isArticles = false;
    switch (value) {
      case 'clients':
        this.isClients = true;
        break;
      case 'articles_folders':
        this.isArticlesFolders = true;
        break;
      case 'articles':
        this.isArticles = true;
        break;
    }
  }
  // TODO: Move user/account data and permissions to separate service
  @Input()
  set userPermissions(userPermissions: object) {
    this.operationsAllowed = new UserPermissions(userPermissions)
  }

  @Input() set filterValue(value: string) {
    this.isFilterActive = !!value;
    if (this.listing) {
      this.listing.filterValue = value
    }
  }

  public get isRoot(): boolean {
    return this.currentPath === '/'
  }
  @ViewChild(CdkVirtualScrollViewport)
  viewport: CdkVirtualScrollViewport;
  @ViewChild('contextMenu') contextMenuElement: ElementRef;
  @ViewChild('downloadFrame', { static: true }) downloadFrameElement: ElementRef;

  @Output() allowFilter = new EventEmitter<boolean>();
  @Output() upload = new EventEmitter<void>();
  @Output() clearFilter = new EventEmitter<void>();
  @Output() back = new EventEmitter<void>();
  @Output() logout = new EventEmitter<object>();
  @Output() doesFolderExist = new EventEmitter<boolean>();

  // todo: sync with css
  public firstItemHeight = 46;
  // !warning: slows virtual scroll on large files amount if used in template.
  // set in template explicitly itemSize="123" and in css .file-row
  public typicalItemHeight = 80;
  private commonDialogConf = {
    width: '22vw',
    height: '19vh',
    autoFocus: false,
    panelClass: 'common-dialog'
  }
  public folderExist = true; // Detect if folder provided in url exist
  public isFilterActive = false; // Detect which content with info about empty files show
  public isClients: boolean;
  public isArticles: boolean;
  public isArticlesFolders: boolean;
  public isItemsLoaded = false;
  public currentPath = '/';
  public operationsAllowed: UserPermissions; // Passed from App component, and tell us what user can do with files
  public activeFile: File; // Element on which was called context menu
  public activeItem: ListItem; // Element on which was called context menu
  public listing: ListingModel;
  public contextMenuStyles = {
    x: `0px`,
    y: `0px`,
    isVisible: false,
  } // We counting position for context menu dynamicly
  public ctrlKeyDown = false; // Detect if control key is pressed
  public shiftKeyDown = false; // Detect if shift key is pressed
  public isArrowUpActive = false; // Detect if go up in focus

  private get prefixForRequest() {
    return 'files' // Prefix for path in api call
  }

  private routerSubscription: SubscriptionLike; // We store it in variable to unsubscribe at destroy
  private sortListSubscription: SubscriptionLike; // We store it in variable to unsubscribe at destroy
  private updateListSubscription: SubscriptionLike; // We store it in variable to unsubscribe at destroy
  private listChangeSubscription: SubscriptionLike;
  private jobChangeSubscription: SubscriptionLike;
  private newCategorySubscription: SubscriptionLike;

  @HostListener('document:keydown', ['$event']) public onKeyDown($event: KeyboardEvent) {
    if ($event['path'] || $event.composedPath) {
      const path = Array.from($event['path'] || ($event.composedPath && event.composedPath()))
      // We need to skip key actions if dialog is open
      if (path.some((el: HTMLElement) =>
        el && el.classList && (el.classList.contains('mat-dialog-container') || el.classList.contains('filter-box')))) {
        return
      }
    }
    this.handleKeyboardEvents($event, 'keydown');
  }
  @HostListener('document:keyup', ['$event']) public onKeyUp($event: KeyboardEvent) {
    if ($event['path'] || $event.composedPath) {
      const path = Array.from($event['path'] || ($event.composedPath && event.composedPath()))
      // We need to skip key actions if dialog is open
      if (path.some((el: HTMLElement) =>
        el && el.classList && (el.classList.contains('mat-dialog-container') || el.classList.contains('filter-box')))) {
        return
      }
    }
    this.handleKeyboardEvents($event, 'keyUp');
  }

  constructor(
    private activeRoute: ActivatedRoute,
    private apiService: ApiService,
    public router: Router,
    private sharedDataService: SharedDataService,
    private uploadService: UploadService,
    public dialog: MatDialog,
    public application: AppComponent,
  ) {
    // Subscription to detect route change, and update items relative on it
    this.routerSubscription = router.events.subscribe(route => {
      if (route instanceof NavigationEnd) { // Skip guards and middle routes
        this.handleClearFilter();
        this.handleRouteChange(route.urlAfterRedirects)
      }
    });

    this.sortListSubscription = this.sharedDataService.sortOrderObservable$.subscribe(
      sortOrder => {
        if(this.isClients) {
          this.listing.sortItems(sortOrder)
        } else {
          this.listing.sortFiles(sortOrder)
        }
      }
    );
    this.updateListSubscription = this.sharedDataService.updateListObservable.subscribe(path => this.fetchItems(path));

    this.listChangeSubscription = this.sharedDataService.addFileToListFixedObservable.subscribe( files => this.addFilesToList(files))
    this.jobChangeSubscription = this.sharedDataService.jobResolvedObservable.subscribe( job => this.listing.checkFileJobUpdate(job))

    // this.sharedDataService.addFileToListObservable.subscribe(
    //   (listItem) => {
    //   })

  }

  private addFilesToList(files: File[]) {
    this.listing.addFiles(files)
    const file = files[0];
    if (file.animateAndFocus) { // check if first file from queue should be focused
      this.listing.clearSelection();
      file.animateAndFocus = false;
      const newFocusedFile = this.listing.files.indexOf(file);
      file.isSelected = true;
      file.isInFocus = true;
      this.listing.lastFocusedFile = newFocusedFile;
      this.viewport.scrollToIndex(newFocusedFile, 'smooth')
    }
  }

  private urlAfterRedirects: string
  public handleRouteChange(url) {
    this.updateInternalStates(this.application.state); //fix not reliable @Input set state
    // If url is passed like a string
    if (!(url instanceof Array)) {
      //this.isClients = url.includes('clients');
      this.urlAfterRedirects = url;
      this.fetchItems(url.split('/').splice(2).join('/')); // Remove prefix and slash from url, like /files or /snapshots
      return
    }
    // If url is passed like an array
    //this.isClients = url.some(el => el.path === 'clients');
    this.urlAfterRedirects = url.join('/');
    this.fetchItems(url.splice(1).join('/')); // Remove prefix from url, like files or snapshots
  }

  private handleFolderItems(articlesByFolder) {
    this.application.folderOwner = articlesByFolder['isOwner'];
    //this.application.folderOwner = articlesByFolder['isOwner'];
    this.handleItems(articlesByFolder['items'])
    this.application.setArticlesFoldersNavigation();
    let openedFolderNavigationItem = new NavigationItem();
    openedFolderNavigationItem.route = '/articles/folder/'+articlesByFolder['folderUUID'];
    openedFolderNavigationItem.label = articlesByFolder['folderName'];
    this.application.addNavigationItem(openedFolderNavigationItem)
  }

  private handleItems(items, snapshotsRoot = false) {
    this.folderExist = true;
    // We need tell app component if path and actions are allowed at path
    this.doesFolderExist.emit(true)
    this.listing.snapshotsRoot = snapshotsRoot;
    this.listing.listItems = items;
    this.activeItem = this.listing.listItems[0];
    // this.listing.files = files;
    // this.activeFile = this.listing.files[0];
    this.isItemsLoaded = true;
    // We hide filter if items array is empty
    this.allowFilter.emit(!!this.listing.listItems.length)

    if(this.isClients) {
      this.application.clearNavigation(); //as of now /clients is a home page, so clear navigation
      setTimeout(() => {
        // better to set viewport size explicitly vs some extra 0.5 bottom offset
        this.viewport.setTotalContentSize(this.firstItemHeight + this.typicalItemHeight * (this.listing.listItems.length + 0.5))
      });
    // } else if(this.isArticlesFolders) {
    //   this.application.setArticlesFoldersNavigation()
    }
  }

  private handleItemsError({ errMsg, exception: { status } }) {
    if (status === 401) {
      // this.sharedDataService.pushNotification({ text: 'Session expired. Please login to proceed.', type: 'error' });
      this.logout.emit({ isExpired: true });
      return
    }
    // if (status === 404) {
    //   this.doesFolderExist.emit(false)
    //   this.folderExist = false
    //   this.isItemsLoaded = true;
    //   return
    // }

    if (errMsg) {
      this.sharedDataService.pushNotification({ text: errMsg, type: 'error' })
    }
    this.isItemsLoaded = true;
  }

  public unEscapeUrlParts(url: string) {
    const urlParts = url.split('/');
    const unEscapedParts = [];
    for (let i = 0; i < urlParts.length; i++) {
      const unEscaped = window.unescape(urlParts[i]);
      unEscapedParts.push(unEscaped);
    }
    return unEscapedParts;
  }

  public fetchItems(pathFromUrl = ''): void {
    if (pathFromUrl.indexOf('/') === 0) {
      pathFromUrl = pathFromUrl.slice(1)
    }
    this.isItemsLoaded = false;
    const path = window.unescape('/' + this.unEscapeUrlParts(pathFromUrl).join('/'))
    this.currentPath = path;
    this.uploadService.setCurrentPath(path); // Update current path for uploader

    if (this.isClients) {
      this.apiService.getClients(path, AppComponent.user.magic_key)
        .subscribe(
          items => this.handleItems(items),
          error => this.handleItemsError(error))
    } else if(this.isArticlesFolders) {
      this.apiService.getArticlesFolders(AppComponent.user.magic_key)
        .subscribe(
          items => this.handleItems(items),
          error => this.handleItemsError(error))
    } else if(this.isArticles) {
      let folder_uuid = pathFromUrl.split('/')[1]
      this.apiService.getArticlesByFolder(AppComponent.user.magic_key,folder_uuid)
        .subscribe(
          items => this.handleFolderItems(items),
          error => this.handleItemsError(error))
    }
  }

  ngOnInit() {
    this.listing = new ListingModel();
    this.application.listingComponent = this; // Left because short of time
    this.handleRouteChange(this.activeRoute.snapshot.url); // Get files for path on which component was initialized
    this.newCategorySubscription = this.sharedDataService.globalEventObservable$.subscribe(
      appEvent => {
        if (appEvent.type === AppEvent.NEW_ARTICLE_CATEGORY) {
          this.showCategoryRenamePopup(-1)
        }
      });
  }

  ngOnDestroy() {
    this.routerSubscription.unsubscribe();
    this.updateListSubscription.unsubscribe();
    this.sortListSubscription.unsubscribe();
    this.listChangeSubscription.unsubscribe();
    this.jobChangeSubscription.unsubscribe();
    this.newCategorySubscription.unsubscribe();
  }

  public handleFileMenu(item: ListItem) {
    this.activeItem = item;
  }

  //public onOpenMenu({ event, item }): void {
  public onContextOpenMenu({ event }, item): void {
    console.log('!!',event)
    if(!event) return;
    event.stopPropagation()
    event.preventDefault()
    this.hideContextMenu();

    this.activeItem = item;

    if (!this.activeItem.isSelected) {
      this.listing.clearSelection()
    }

    const contextMenuStyles = window.getComputedStyle(this.contextMenuElement.nativeElement)
    const contextMenuHeight = parseInt(contextMenuStyles.height, 10);
    const contextMenuWidth = parseInt(contextMenuStyles.width, 10);

    let usedYOffset = 0;
    let usedXOffset = 0;

    // Count if menu will overvaly vertically, to avoid scrolls and placement out of viewport
    if (window.innerHeight < event.clientY + contextMenuHeight) {
      usedYOffset = contextMenuHeight;
    }

    // Count if menu will overvaly horizontally, to avoid scrolls and placement out of viewport
    if (window.innerWidth < event.clientX + contextMenuWidth) {
      usedXOffset = contextMenuWidth;
    }

    this.contextMenuStyles = {
      x: `${event.clientX - usedXOffset}px`,
      y: `${event.clientY - usedYOffset}px`,
      isVisible: true,
    }
  }
  public hideContextMenu(): void {
    this.contextMenuStyles.isVisible = false;
  }

  public mouseOut({ event }): void {
    if (!this.contextMenuStyles.isVisible) {
      return
    }
    // We need it to save context menu visible while user moving pointer out of file row
    if (event.relatedTarget) {
      const element = (event.relatedTarget as HTMLElement)
      if (!(element.classList && (element.classList.contains('context-menu-item') || element.classList.contains('context-menu')))) {
        this.hideContextMenu()
      }
    } else {
      this.hideContextMenu()
    }
  }

  public getFiles(file: File) {
    if (file.isSnapshot && file.isFile) {
      return
    }
    this.isArrowUpActive = false;
    // Encoding path to avoid erros with symbols
    const escapedPath = '/' + window.encodeURIComponent(window.escape(file.full_path.slice(1)));
    if (this.isClients) {
      // If dbclick at file we download it
      if (file.isFile) {

        if (!this.operationsAllowed.isDownloadAllowed) {
          return;
        }

        this.apiService.getDownloadLink([file])
          .subscribe(({ link }) => this.handleDownloadLink(link));
        return
      }
      if (file.isShared || file.isDirectory) {
        this.router.navigateByUrl(`/files${escapedPath}`)
        return
      }
    }
    this.router.navigateByUrl(`/snapshots${escapedPath}`)

  }

  private getActionTagret(): File[] {
    // Detect if operation for one file or all selected, and return target
    return this.activeFile.isSelected ? this.listing.selectedFiles : [this.activeFile]
  }

  private getActionItemTarget(): ListItem[] {
    // Detect if operation for one file or all selected, and return target
    return this.activeItem.isSelected ? this.listing.selectedListItems : [this.activeItem]
  }

  public handleReportDownload() {
    let item:ListItem = this.getActionItemTarget()[0];
    let params = [];
    params.push('magic_key='+item.itemFullData['magic_key']);
    params.push('gid='+item.itemFullData['native_group']['id']);
    params.push('from='+item.itemFullData['created_time']);
    params.push('to='+Math.round((new Date().getTime())/1000));
    this.downloadFrameElement.nativeElement.setAttribute('src', '/api/1.0/clients/download_log?'+params.join('&'))
    this.sharedDataService.pushNotification({ text: 'The download will start shortly.', type: 'success' })
  }

  private handleDownloadLink(link: string) {
    // To download file or folder we pass response from api call to iframe as src attribute
    this.downloadFrameElement.nativeElement.setAttribute('src', link)
    this.sharedDataService.pushNotification({ text: 'The download will start shortly.', type: 'success' })
  }

  private clearAfterAction() {
    this.listing.clearSelection();
    this.hideContextMenu();
  }

  public handleDownload() {
    this.apiService
      .getDownloadLink(this.getActionTagret())
      .subscribe(
        ({ link }) => this.handleDownloadLink(link),
        error => this.handleItemsError(error)
      );
    this.clearAfterAction();
  }

  public handleProfileInfo() {
    console.log('handleProfileInfo',this.activeItem);
    const dialogRef = this.dialog.open(DialogCanvasComponent, {
      minWidth: this.application.layoutMatches['isHandsetDefaultLayout'] ? '95%' : '65%',
      height: 'auto',
      //...this.commonDialogConf,
      //maxWidth: '22vw',
      data: {
        application: this.application,
        state: 'profile-info-popup',
        payload: {item: this.activeItem},
      }
    } as MatDialogConfig);
    dialogRef.addPanelClass('common-dialog');
    dialogRef.addPanelClass('common-dialog-medium-form');
    dialogRef.afterOpened().subscribe(() => {
      (dialogRef.componentInstance as DialogCanvasComponent).isOpened = true;
    })
  }

  public handleZeroPoints() {
    console.log('this.activeItem',this.activeItem);
    const dialogRef = this.dialog.open(DialogCanvasComponent, {
      ...this.commonDialogConf,
      maxWidth: '22vw',
      data: {
        application: this.application,
        state: 'profile-points-form',
        payload: {item: this.activeItem.itemFullData},
      }
    } as MatDialogConfig);
    dialogRef.addPanelClass('common-dialog');
    dialogRef.addPanelClass('common-dialog-medium-form');
    dialogRef.afterOpened().subscribe(() => {
      (dialogRef.componentInstance as DialogCanvasComponent).isOpened = true;
    })
  }

  public handleZip() {
    const dialogRef = this.dialog.open(ZipDialogComponent, {
      ...this.commonDialogConf,
      data: { path: this.currentPath, files: this.getActionTagret() }
    } as MatDialogConfig);
    dialogRef.afterClosed().subscribe(result => {
      clearFocus()
      if (result) {
        if (result.isSuccess) {
          // this.sharedDataService.pushNotification({ text: `Zipping started`, type: 'info' })
          // this.fetchItems(this.currentPath)
          this.addFilesToList([result.resultFile])
        } else {
          this.handleItemsError(result.error.exception)
        }
      }
    });
    this.clearAfterAction()
  }

  public handleUnzip(): void {
    this.apiService
      .unzipArchive(this.activeFile)
      .subscribe(
        (data) => {
          // this.fetchItems(this.currentPath);
          // this.sharedDataService.pushNotification({ text: `Unzip for ${this.activeFile.name} started`, type: 'info' })
          const futureFile: File = this.makeInstantFile(this.currentPath, this.activeFile.name.replace(/\.zip$/i, ''), 'd');
          futureFile.animateAndFocus = true;
          futureFile.isPending = true;
          futureFile.jobID = data['job_id'];
          this.addFilesToList([futureFile]);
        },
        error => this.handleItemsError(error)
      );
    this.clearAfterAction();
  }

  public handleRename() {
    this.sharedDataService.pushNotification({ text: `This functionality will be available in two weeks time.`, type: 'info' })
    return;
    const dialogRef = this.dialog.open(RenameDialogComponent, {
      ...this.commonDialogConf,
      data: { file: this.activeFile },
    } as MatDialogConfig);
    dialogRef
      .afterClosed()
      .subscribe((result: any) => {
        clearFocus();
        if (result) {
          if (result.isSuccess) {
            // this.fetchItems(this.currentPath)
            this.sharedDataService.pushNotification({ text: `${this.activeFile.name} was renamed successfully`, type: 'success' })
          } else {
            this.handleItemsError(result.error.exception)
          }
        }
      });
    this.clearAfterAction();
  }

  public handleDelete() {
    this.sharedDataService.pushNotification({ text: `This functionality will be available in two weeks time.`, type: 'info' })
    return;
    const target: File[] = this.getActionTagret();
    const dialogRef = this.dialog.open(DeleteConfirmationComponent, {
      ...this.commonDialogConf,
      width: '28vw',
      height: '150px',
      minHeight: '150px',
      data: {
        target
      }
    } as MatDialogConfig);
    dialogRef.afterClosed()
      .subscribe((result) => {
        if (result && result.isConfirmed) {
          this.apiService
            .deleteFiles(target)
            .subscribe(
              (data) => {
                target.forEach(file => {
                  file.isPending = true;
                  file.jobID = data['job_id'];
                  file.jobNextAction = 'delete';
                })
                // this.fetchItems(this.currentPath);
                // this.sharedDataService.pushNotification({ text: `Deletion has started.`, type: 'info' })
              },
              error => this.handleItemsError(error)
            );
        }
      })
    this.clearAfterAction();
  }

  public handleCopy() {
    this.listing.clearMoveBuffer();
    const files = this.getActionTagret()
    // We store it in buffer and wait for paste operation
    this.listing.copyBuffer = Array.from(new Set([...this.listing.copyBuffer, ...files]))
    // To hold files after refresh we put it in sessionStorage
    window.sessionStorage.setItem('copyBuffer', JSON.stringify(this.listing.copyBuffer));
    this.sharedDataService.pushNotification({ text: `Select destination folder`, type: 'info' })

    this.clearAfterAction();
  }

  public handleCut() {
    this.listing.clearCopyBuffer();
    const files = this.getActionTagret()
    // The same as for copy
    this.listing.moveBuffer = Array.from(new Set([...this.listing.moveBuffer, ...files]))
    window.sessionStorage.setItem('moveBuffer', JSON.stringify(this.listing.moveBuffer));
    this.sharedDataService.pushNotification({ text: `Select destination folder`, type: 'info' })
    this.clearAfterAction();
  }

  public handlePaste(): void {
    if (this.listing.copyBuffer.length) {
      if (this.listing.copyBuffer.some(file => this.currentPath.includes(file.full_path))) {
        this.sharedDataService.pushNotification({ text: 'You cannot move files or folders into the source folder.', type: 'warning' })
        return
      }
      this.apiService
        .copyFiles(
          this.listing.copyBuffer,
          this.currentPath,
        )
        .subscribe(
          (data) => {
            const newFiles: File[] = this.getClonedFilesForPath(this.listing.copyBuffer, this.currentPath);
            newFiles.map(file => {
              file.isPending = true;
              file.jobID = data['job_id'];
            })
            newFiles[0].animateAndFocus = true;
            this.addFilesToList(newFiles)
            this.listing.clearCopyBuffer()
            // this.fetchItems(this.currentPath)
            // this.sharedDataService.pushNotification({ text: `Copy started`, type: 'info' })
          },
          error => this.handleItemsError(error)
        );
    } else {
      // It is cut operation
      if (this.listing.moveBuffer.some(file => this.currentPath.includes(file.full_path))) {
        this.sharedDataService.pushNotification({ text: 'You cannot move files or folders into the source folder.', type: 'warning' })
        return
      }
      this.apiService
        .moveFiles(
          this.listing.moveBuffer,
          this.currentPath,
        )
        .subscribe(
          (data) => {
            const newFiles: File[] = this.getClonedFilesForPath(this.listing.moveBuffer, this.currentPath);
            newFiles.map(file => {
              file.isPending = true;
              file.jobID = data['job_id'];
            })
            newFiles[0].animateAndFocus = true;
            this.addFilesToList(newFiles)
            this.listing.clearMoveBuffer()
            // this.fetchItems(this.currentPath)
            // this.sharedDataService.pushNotification({ text: `Cut started`, type: 'info' });
          },
          error => this.handleItemsError(error)
        );
    }
  }

  private getClonedFilesForPath(files: File[], path: string): File[] {
    const retFiles: File[] = [];
    files.map(file => {
      const name = file.name;
      const type = file.type;
      const owner = file.owner;
      const perm = 0;
      const time = (new Date()).getTime();
      const size = file.size;
      const retFile: File = File.genFile({name, type, owner, perm, time, size, path});
      retFile.full_path = path + '/' + retFile.name;
      retFile.full_path_old = path + '/' + retFile.name;
      retFiles.push(retFile);
    })
    return retFiles;
  }

  public handleRestore(path): void {
    const restoreBuffer: Array<File> = [];
    this.listing.restoreBuffer.forEach(file => {
      const fileClone = { ...file } as File;
      fileClone.full_path_old = fileClone.full_path;
      if (!path) { // restore in original place
        fileClone.full_path = '/' + fileClone.full_path.split('/').slice(2).join('/');
      } else { // restore selected file or folder in specific location
        fileClone.full_path = path + '/' + fileClone.full_path.split('/').pop();
      }
      restoreBuffer.push(fileClone);
    });

    this.apiService
      .restoreFiles(
        restoreBuffer,
      )
      .subscribe(
        (data) => {
          if (this.isClients) {
            const newFiles: File[] = this.getClonedFilesForPath(restoreBuffer, this.currentPath);
            newFiles.map(file => {
              file.isPending = true;
              file.jobID = data['job_id'];
            })
            newFiles[0].animateAndFocus = true;
            this.addFilesToList(newFiles)
            // this.fetchItems(this.currentPath)
          }
          this.sharedDataService.pushNotification({ text: `Restore started`, type: 'info' });
        },
        error => this.handleItemsError(error)
      );
    this.listing.clearRestoreBuffer()

  }

  public handleRestoreInOriginal() {
    this.handleCopyInBuffer(true);
    this.handleRestore('');
  }
  public handleCopyInBuffer(skipNotifcation = false) {
    this.listing.clearCopyBuffer();
    this.listing.clearMoveBuffer();
    const files = this.getActionTagret()
    // We store it in buffer and wait for restore operation
    this.listing.restoreBuffer = Array.from(new Set([...this.listing.restoreBuffer, ...files]))
    // To hold files after refresh we put it in sessionStorage
    window.sessionStorage.setItem('restoreBuffer', JSON.stringify(this.listing.restoreBuffer));
    if (!skipNotifcation) {
      this.sharedDataService.pushNotification({ text: `Select destination folder`, type: 'info' })
    }
    this.clearAfterAction();
  }

  public handleUpload(): void {
    // Event to open upload dialog
    this.upload.emit()
  }
  public handleClearFilter(): void {
    // Event to clear file filter
    this.clearFilter.emit()
  }

  public handleGoToAll() {
    // Handle click go home when empty folder
    this.back.emit()
  }

  public findFileSelectedWithArrows(addition: number) {
    // Detect current cursor position at files
    const latestFocusedFile = this.listing.files.findIndex(file => file.isInFocus);
    if (latestFocusedFile !== -1) {
      // Addition is -1 or 1 , opposite to upper and lower file
      const target = latestFocusedFile + addition;
      if (target > this.listing.files.length - 1) {
        return
      }
      if (target >= 0) {
        this.isArrowUpActive = false;
        this.selectFile(this.listing.files[target]);
      } else {
        this.listing.clearSelection();
        this.isArrowUpActive = true;
      }
    } else { // select first file
      this.isArrowUpActive = false;
      this.selectFile(this.listing.files[0])
    }
  }

  public clickOnFile(event, targetFile: File) {
    // detect click outside file row
    let tagName = '';
    if (event.target) { // Firefox, Opera, Google Chrome and Safari
      tagName = event.target.tagName;
    } else if (event.explicitOriginalTarget) { // Firefox
      tagName = event.explicitOriginalTarget.tagName;
    } else if (event.srcElement) {  // Internet Explorer
      tagName = event.srcElement.tagName;
    }

    if (tagName === "APP-LISTING-ITEM") {
      return;
    }
    this.selectFile(targetFile);
  }

  public selectFile(targetFile: File) {
    if (!targetFile) {
      this.listing.clearSelection();
      return
    }
    this.isArrowUpActive = false;

    const lastFocusedFile = this.listing.lastFocusedFile;

    let newFocusedFile = -1;

    this.listing.files.forEach((file, index) => {
      if (targetFile.name === file.name && targetFile.type === file.type) {
        file.isInFocus = true;
        file.isSelected = !file.isSelected;
        newFocusedFile = index;
      } else {
        file.isInFocus = false;
        if (!this.ctrlKeyDown) {
          file.isSelected = false;
        }
      }
    })

    if (targetFile.isStorage) {
      this.listing.lastFocusedFile = newFocusedFile;
      return
    }

    if (newFocusedFile !== -1) {
      if (lastFocusedFile !== -1 && this.shiftKeyDown) {
        const minSelectedIdx = Math.min(newFocusedFile, lastFocusedFile)
        const maxSelectedIdx = Math.max(newFocusedFile, lastFocusedFile)
        this.listing.files.forEach((file, index) => {
          file.isSelected = index >= minSelectedIdx && index <= maxSelectedIdx;
        })
      }
      const fileFocusScrollThreshold = this.typicalItemHeight * 2; // distance from edges when scroll to the file
      const verticalSize = this.viewport.measureScrollOffset() + this.viewport.getViewportSize();
      const itemPosition = newFocusedFile * this.typicalItemHeight;
      if ( (verticalSize - fileFocusScrollThreshold) < itemPosition ||
        (this.viewport.measureScrollOffset()) > itemPosition
      ) {
        this.viewport.scrollToIndex(newFocusedFile, 'smooth');
      }
    }

    if (newFocusedFile !== -1 && (lastFocusedFile === -1 || !this.shiftKeyDown)) {
      this.listing.lastFocusedFile = newFocusedFile; // We store last focused file to known on which was enter
      this.activeFile = targetFile; // todo: double check
    }
  }

  public clickEditFolder(event, targetItem: ListItem) {
    let folderData = targetItem['itemFullData'];
    const dialogRef = this.dialog.open(DialogCanvasComponent, {
      //...this.commonDialogConf,
      height: '50%',
      minHeight: '50%',
      minWidth: this.application.layoutMatches['isHandsetDefaultLayout'] ? '90%' : '65%',
      data: {
        application: this.application,
        state: 'course-edit',
        payload: {
          folderData: folderData,
          mode: 'edit',
          doFolderInsert: false,
        }
      }
    } as MatDialogConfig);
    dialogRef.addPanelClass('common-dialog');
    dialogRef.addPanelClass('common-dialog-medium-form');
    dialogRef.afterOpened().subscribe(() => {
      (dialogRef.componentInstance as DialogCanvasComponent).isOpened = true;
    })
    dialogRef.afterClosed()
      .subscribe((confirmed) => {
        if(confirmed) {
          this.sharedDataService.updateList('/')
        }
      })
  }

  public clickCourseShares(event, targetItem: ListItem) {
    //console.log('clickCourseShares',targetItem)
    const dialogRef = this.dialog.open(DialogCanvasComponent, {
      //...this.commonDialogConf,
      height: 'auto',
      data: {
        application: this.application,
        state: 'course-share',
        payload: {
          listItem: targetItem,
          folderUUID: targetItem['itemFullData']['uuid'],
        }
      }
    } as MatDialogConfig);
    dialogRef.addPanelClass('common-dialog');
    dialogRef.addPanelClass('common-dialog-wider-form');
    dialogRef.afterOpened().subscribe(() => {
      (dialogRef.componentInstance as DialogCanvasComponent).isOpened = true;
    })
  }

  public clickOnAlrticlesFolder(event, targetItem: ListItem) {
    this.router.navigateByUrl('/articles/folder/'+targetItem['itemFullData']['uuid'])
    return
  }

  public clickOnArticleItem(event, targetItem: ListItem, openStaticPage: boolean = false, openInEditMode: boolean = false) {
    let folderUUID = this.urlAfterRedirects.split('/')[2];
    //console.log('this.urlAfterRedirects',this.urlAfterRedirects);
    if(openStaticPage || (!this.application.isTrainerLogin() && targetItem['itemFullData']['static_html_alias'])) {
      // let staticUrl = window.location.pathname.split('/').reverse().slice(2).reverse().join('/')+
      //   '/'+targetItem['itemFullData']['uuid'] +
      //   '/'+targetItem['itemFullData']['static_html_alias'];
      //todo: refactor r_articles redirect code duplication from article-view.component
      let staticUrl = '/'+AppComponent.appLang+'/r_articles/'+environment['gid']+'/'+AppComponent.appLang+'/'+targetItem['itemFullData']['uuid']+'/'+targetItem['itemFullData']['static_html_alias'];
      window.open(staticUrl, '_blank')
    } else {
      let articleUrl = '/articles/item/'+folderUUID+'/'+targetItem['itemFullData']['uuid']
      articleUrl+= openInEditMode ? '/edit' : '';
      this.router.navigateByUrl(articleUrl)
    }
    return
  }

  public exportArticleItem(event, targetItem: ListItem) {
    const dialogRef = this.dialog.open(DialogCanvasComponent, {
      //...this.commonDialogConf,
      height: 'auto',
      data: {
        application: this.application,
        state: 'article-export',
        payload: {
          listItem: targetItem,
          uuid: targetItem['itemFullData']['uuid'],
          folderUUID: this.urlAfterRedirects.split('/')[2],
        }
      }
    } as MatDialogConfig);
    dialogRef.addPanelClass('common-dialog');
    dialogRef.addPanelClass('common-dialog-medium-form');
    dialogRef.afterOpened().subscribe(() => {
      (dialogRef.componentInstance as DialogCanvasComponent).isOpened = true;
    })
  }

  public showCategoryRenamePopup(categoryIdx) {
    console.log('categoryIdx',categoryIdx);
    const dialogRef = this.dialog.open(DialogCanvasComponent, {
      //...this.commonDialogConf,
      height: 'auto',
      data: {
        application: this.application,
        state: 'articles-category-rename',
        payload: {
          categoryIdx: categoryIdx,
          listItems: this.listing.listItems,
          folderUUID: this.urlAfterRedirects.split('/')[2],
        }
      }
    } as MatDialogConfig);
    dialogRef.addPanelClass('common-dialog');
    dialogRef.addPanelClass('common-dialog-medium-form');
    dialogRef.afterOpened().subscribe(() => {
      (dialogRef.componentInstance as DialogCanvasComponent).isOpened = true;
    })
  }
  public newArticle(categoryIdx) {
    let articleData = {};
    articleData['article'] = [{type: 'title', value: ''}];
    let articleUUID = uuid().toUpperCase();
    articleData['uuid'] = articleUUID;
    articleData['article_type'] = "A";
    articleData['mdate'] = (new Date()).getTime()/1000;
    const dialogRef = this.dialog.open(DialogCanvasComponent, {
      //...this.commonDialogConf,
      height: '50%',
      minHeight: '50%',
      minWidth: this.application.layoutMatches['isHandsetDefaultLayout'] ? '90%' : '65%',
      data: {
        application: this.application,
        state: 'article-item-edit',
        payload: {
          articleData: articleData,
          folderUUID: this.urlAfterRedirects.split('/')[2],
          idx: 0,
          mode: 'insert',
          doArticleInsert: true,
          categoryIdx: categoryIdx
        }
      }
    } as MatDialogConfig);
    dialogRef.addPanelClass('common-dialog');
    dialogRef.addPanelClass('common-dialog-medium-form');
    dialogRef.afterOpened().subscribe(() => {
      (dialogRef.componentInstance as DialogCanvasComponent).isOpened = true;
    })
    dialogRef.afterClosed()
      .subscribe((confirmed) => {
        if(confirmed) {
          //ignore. article created in popup
        }
      })

  }
  public showReorderPopup(categoryIdx) {
    console.log('categoryIdx',categoryIdx);
    const dialogRef = this.dialog.open(DialogCanvasComponent, {
      //...this.commonDialogConf,
      height: 'auto',
      data: {
        application: this.application,
        state: 'articles-category-reorder',
        payload: {
          listItems: this.listing.listItems,
          folderUUID: this.urlAfterRedirects.split('/')[2],
        }
      }
    } as MatDialogConfig);
    dialogRef.addPanelClass('common-dialog');
    dialogRef.addPanelClass('common-dialog-medium-form');
    dialogRef.afterOpened().subscribe(() => {
      (dialogRef.componentInstance as DialogCanvasComponent).isOpened = true;
    })
  }
  public deleteArticlesCategoryOrItem(state,categoryIdx,itemIdx) {
    const dialogRef = this.dialog.open(DialogCanvasComponent, {
      //...this.commonDialogConf,
      height: 'auto',
      data: {
        application: this.application,
        state: state,
        payload: {
          listItems: this.listing.listItems,
          folderUUID: this.urlAfterRedirects.split('/')[2],
        }
      }
    } as MatDialogConfig);
    dialogRef.addPanelClass('common-dialog');
    dialogRef.addPanelClass('common-dialog-medium-form');
    dialogRef.afterOpened().subscribe(() => {
      (dialogRef.componentInstance as DialogCanvasComponent).isOpened = true;
    })
    dialogRef.afterClosed()
      .subscribe((confirmed) => {
        if(confirmed) {
          if(state === 'articles-category-delete') {
            this.listing.listItems.splice(categoryIdx, 1);
          }
          let newArticlesPositions = this.listing.listItems.map( (category, index) => {
            let ret = {};
            ret['sorder'] = index;
            ret['group_name'] = category['group_name'];
            ret['group_key'] = category['group_key'];
            ret['group_image_src'] = category['group_image_src'];
            if(state === 'article-delete' && index === categoryIdx) {
              category['articles'].splice(itemIdx, 1);
            }
            ret['articles_uuids'] = category['articles'].map( (article) => {
              return article['itemFullData']['uuid'];
            });
            return ret;
          })
          this.application.apiService
            .updateArticlesGroups(newArticlesPositions, this.urlAfterRedirects.split('/')[2])
            .subscribe(
              data => {
                let notification = new FS3Notification();
                notification.type = 'success';
                notification.text = 'Updated';
                this.sharedDataService.pushNotification(notification);
              },
              error => {
                const { exception } = error;
                if (exception && exception.error && exception.error.message) {
                  let notification = new FS3Notification();
                  notification.type = 'error';
                  notification.text = exception.error.message;
                  this.sharedDataService.pushNotification(notification);
                }
              }
            )
        }
      })
  }

  public clickOnAlrticleReorder(event, targetItem: ListItem, categoryIdx, itemIdx) {
    console.log('categoryIdx, itemIdx',categoryIdx, itemIdx);
    const dialogRef = this.dialog.open(DialogCanvasComponent, {
      //...this.commonDialogConf,
      height: 'auto',
      data: {
        application: this.application,
        state: 'articles-reorder',
        payload: {
          listItems: this.listing.listItems,
          folderUUID: this.urlAfterRedirects.split('/')[2],
        }
      }
    } as MatDialogConfig);
    dialogRef.addPanelClass('common-dialog');
    dialogRef.addPanelClass('common-dialog-medium-form');
    dialogRef.afterOpened().subscribe(() => {
      (dialogRef.componentInstance as DialogCanvasComponent).isOpened = true;
    })
  }

  public clickOnItem(event, targetFile: ListItem) {
    // detect click outside file row
    let tagName = '';
    if (event.target) { // Firefox, Opera, Google Chrome and Safari
      tagName = event.target.tagName;
    } else if (event.explicitOriginalTarget) { // Firefox
      tagName = event.explicitOriginalTarget.tagName;
    } else if (event.srcElement) {  // Internet Explorer
      tagName = event.srcElement.tagName;
    }

    if (tagName === "APP-LISTING-ITEM") {
      return;
    }
    this.selectItem(targetFile);
  }

  public selectItem(targetFile: ListItem) {
    if (!targetFile) {
      this.listing.clearSelection();
      return
    }
    this.isArrowUpActive = false;

    const lastFocusedFile = this.listing.lastFocusedFile;

    let newFocusedFile = -1;

    this.listing.listItems.forEach((file, index) => {
      if (targetFile.full_path === file.full_path && targetFile.type === file.type) {
        file.isInFocus = true;
        file.isSelected = !file.isSelected;
        newFocusedFile = index;
      } else {
        file.isInFocus = false;
        if (!this.ctrlKeyDown) {
          file.isSelected = false;
        }
      }
    })

    if (targetFile.isStorage) {
      this.listing.lastFocusedFile = newFocusedFile;
      return
    }

    if (newFocusedFile !== -1) {
      if (lastFocusedFile !== -1 && this.shiftKeyDown) {
        const minSelectedIdx = Math.min(newFocusedFile, lastFocusedFile)
        const maxSelectedIdx = Math.max(newFocusedFile, lastFocusedFile)
        this.listing.listItems.forEach((file, index) => {
          file.isSelected = index >= minSelectedIdx && index <= maxSelectedIdx;
        })
      }
      const fileFocusScrollThreshold = this.typicalItemHeight * 2; // distance from edges when scroll to the file
      const verticalSize = this.viewport.measureScrollOffset() + this.viewport.getViewportSize();
      const itemPosition = newFocusedFile * this.typicalItemHeight;
      if ( (verticalSize - fileFocusScrollThreshold) < itemPosition ||
        (this.viewport.measureScrollOffset()) > itemPosition
      ) {
        this.viewport.scrollToIndex(newFocusedFile, 'smooth');
      }
    }

    if (newFocusedFile !== -1 && (lastFocusedFile === -1 || !this.shiftKeyDown)) {
      this.listing.lastFocusedFile = newFocusedFile; // We store last focused file to known on which was enter
      this.activeItem = targetFile; // todo: double check
    }
  }

  private handleKeyboardEvents(event: KeyboardEvent, direction: string) {
    const isKeydown = direction === 'keydown';
    const keyPressed = event.key.toLowerCase();

    // prevent page elements selection or scrolling by arrows
    if (keyPressed === 'a'
      || keyPressed === 'arrowdown' || keyPressed === 'down'
      || keyPressed === 'arrowup' || keyPressed === 'up'
    ) {
      event.preventDefault();
    }

    if (keyPressed === 'control' || keyPressed === 'meta') {
      this.ctrlKeyDown = isKeydown;
    }

    // if (this.ctrlKeyDown && !isKeydown && keyPressed === 'a') {
    if (this.ctrlKeyDown && keyPressed === 'a') {
      // if (this.listing.selectedFiles.length && this.isFiles || (!this.isFiles && !this.isRoot)) {
      if (this.isClients) {
        this.listing.selectAll()
      }
    }

    if (this.ctrlKeyDown && keyPressed === 'f') {
      event.preventDefault();
      this.ctrlKeyDown = false;
      if (document.getElementById('filterInput')) {
        document.getElementById('filterInput').focus()
      }
    }

    if (keyPressed === 'shift') {
      this.shiftKeyDown = isKeydown;
    }

    if (isKeydown) {
      if (keyPressed === 'arrowdown' || keyPressed === 'down') {
        this.findFileSelectedWithArrows(+1);
      }

      if (keyPressed === 'arrowup' || keyPressed === 'up') {
        this.findFileSelectedWithArrows(-1);
      }

      if (keyPressed === 'escape') {
        this.listing.clearSelection();
      }

      if (keyPressed === 'enter') {
        // Detect if key enter was pressed at file row or go up row
        if (!this.isArrowUpActive && this.listing.lastFocusedFile !== -1) {
          this.getFiles(this.listing.files[this.listing.lastFocusedFile]);
        } else {
          this.goUp();
        }
      }

      if (keyPressed === 'delete' || keyPressed === 'del') {
        this.activeFile = this.listing.lastSelectedFile;
        this.handleDelete()
      }
    }

    // if (!isKeydown && this.ctrlKeyDown) {
    if (this.ctrlKeyDown) {
      if (keyPressed === 'c') {
        this.activeFile = this.listing.lastSelectedFile;
        if (this.isClients) {
          this.handleCopy();
        } else { // snapshots
          this.handleCopyInBuffer();
        }
      }
      if (keyPressed === 'x') {
        if (this.isClients) {
          this.activeFile = this.listing.lastSelectedFile;
          this.handleCut();
        }
      }
      if (keyPressed === 'v') {
        if (this.isClients) {
          this.handlePaste();
        }
      }
    }
  }

  public goUp(): void {
    if(this.isRoot) return;
    this.isArrowUpActive = false;
    const parts = this.currentPath.split('/');
    const prefix = '/clients';
    parts.pop()
    // Detect if we need go to root
    if (parts.length <= 1 && !parts[0]) {
      this.router.navigateByUrl(prefix + '/')
      return
    }
    parts.shift()
    const encodedParts = window.encodeURIComponent(window.escape(parts.join('/')));
    this.router.navigateByUrl(`${prefix}/${encodedParts}`)
  }

  public makeInstantFile(path, name, type): File {
    // const name = name;
    // const type = type;
    const owner = '';
    const perm = 0;
    const time = (new Date()).getTime();
    const size = 0;
    const file = File.genFile({ name, type, owner, perm, time, size, path });
    // file.isPending = true;
    return file
  }
}
